
import React, { memo, useContext } from 'react'

import { widgetWindowsSelector } from '@/entities/widgetWindows'
import { Icon, ToggleButton } from '@/shared/components'
import { IconButton } from '@/shared/components/IconButton/IconButton'
import { MosaicWindowContext, MosaicWindowProps, WidgetType } from '@/shared/components/Mosaic'
import { useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faChevronRight, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { Grid, styled, Typography } from '@mui/material'

export type MosaicWindowToolbarProps = Pick<MosaicWindowProps<WidgetType>, 'title' | 'id'> & {
  onWindowClose: () => void
  isAdditionalToolbarEnabled?: boolean
  additionalControlsOpen?: boolean
  toolbarActions?: React.ReactNode
}

const InternalMosaicWindowToolbar: React.FC<MosaicWindowToolbarProps> = ({ id, title, onWindowClose, isAdditionalToolbarEnabled, additionalControlsOpen, toolbarActions }) => {
  const { mosaicWindowActions } = useContext<MosaicWindowContext>(MosaicWindowContext)
  const navigations = useAppSelector(state => widgetWindowsSelector.selectById(state, id)?.navigations) || []

  return (
    <StyledMosaicWindowToolbar>
      <Grid container justifyContent={'space-between'} alignItems={'center'} wrap={'nowrap'}>
        <StyledHeaderGrid item xs style={{ flex: 1, minWidth: 0 }}>
          {navigations ? navigations.map(({ icon, title: navTitle, uiColor }, index) => {
            const navItemIcon = icon ? <StyledHeaderIcon icon={icon} sx={{ color: uiColor, maxHeight: pxToRem(16) }} /> : null
            const navItemTitle = <StyledWindowTitle variant="h2">{navTitle}</StyledWindowTitle>
            let navChevron = null

            if (index < navigations.length - 1) {
              navChevron = <StyledHeaderCheveron icon={faChevronRight} />
            }

            return <React.Fragment key={index}>
              {navItemIcon}
              {navItemTitle}
              {navChevron}
            </React.Fragment>
          }) : <StyledWindowTitle variant="h2">{title}</StyledWindowTitle>}
        </StyledHeaderGrid>
        <StyledToolbarActionsContainer item>
          {toolbarActions}
          {isAdditionalToolbarEnabled && (
            <ToggleButton
              value="checked"
              primary
              text
              selected={additionalControlsOpen}
              onChange={() => {
                mosaicWindowActions.setAdditionalControlsOpen('toggle')
              }}
            >
              Toolbar
            </ToggleButton>
          )}
          <StyledIconButton
            round
            onClick={onWindowClose}
          >
            <StyledCloseIcon icon={faXmark} />
          </StyledIconButton>
        </StyledToolbarActionsContainer>
      </Grid>
    </StyledMosaicWindowToolbar>
  )
}

export const MosaicWindowToolbar = memo(InternalMosaicWindowToolbar, (prevProps, nextProps) => {
  return prevProps.title === nextProps.title
    && prevProps.additionalControlsOpen === nextProps.additionalControlsOpen
    && prevProps.isAdditionalToolbarEnabled === nextProps.isAdditionalToolbarEnabled
    && prevProps.toolbarActions === nextProps.toolbarActions
    && prevProps.onWindowClose === nextProps.onWindowClose
    && prevProps.id === nextProps.id
})

const StyledHeaderGrid = styled(Grid)`
  height: 100%;
  display: inline-flex;
  align-items: center;
`

const StyledHeaderIcon = styled(Icon)`
  font-size: ${({ theme }) => theme.typography.pxToRem(18)};
  margin-right: ${({ theme }) => theme.typography.pxToRem(5)};
`

const StyledHeaderCheveron = styled(Icon)`
  font-size: ${({ theme }) => theme.typography.pxToRem(14)};
  color: ${({ theme }) => theme.palette.text.secondary};
  margin: 0 6px 2px 6px;
`

const StyledCloseIcon = styled(Icon)`
  font-size: ${({ theme }) => theme.typography.pxToRem(16)};
`

const StyledToolbarActionsContainer = styled(Grid)`
  display: inline-flex;
  justify-content: center;
  align-items: center;
`

const StyledWindowTitle = styled(Typography)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`

const StyledMosaicWindowToolbar = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  height: ${({ theme }) => theme.typography.pxToRem(35)};
  color: ${({ theme }) => theme.palette.text.primary};
  background-color: ${({ theme }) => theme.palette.custom.mosaicWindowToolbar.backgroundColor};
  box-shadow: ${({ theme }) => theme.palette.custom.mosaicWindowToolbar.boxShadow};
  padding: 0 ${pxToRem(8)};
`

const StyledIconButton = styled(IconButton)`
  margin: 0 ${pxToRem(7)} 0 ${pxToRem(8)};
`